import { useNavigate } from "react-router-dom";
import PublicShell from "../components/public/PublicShell";
import { useSeo } from "../lib/useSeo";

const CSS = `
.agz-landing .public-page{padding:72px 0 48px}
.agz-landing .public-page h1{font-size:clamp(38px,5.5vw,64px);letter-spacing:-.035em;margin-bottom:22px;max-width:900px}
.agz-landing .public-page .eyebrow{display:inline-block;font-size:12px;font-weight:600;letter-spacing:.14em;color:var(--muted);text-transform:uppercase;margin-bottom:22px}
.agz-landing .public-page .intro{font-size:clamp(17px,1.6vw,20px);color:var(--ink-soft);line-height:1.55;max-width:820px;margin-bottom:64px}
.agz-landing .principles{display:grid;gap:18px;margin-top:28px}
.agz-landing .prin{background:var(--card);border:1px solid var(--line);border-radius:var(--r-card);padding:28px clamp(24px,3vw,36px);box-shadow:var(--sh-xs);display:grid;grid-template-columns:auto 1fr;gap:24px;align-items:start;transition:transform .18s,box-shadow .2s,border-color .2s}
.agz-landing .prin:hover{transform:translateY(-2px);box-shadow:var(--sh-sm);border-color:#cfc9bd}
.agz-landing .prin .n{font-family:var(--sans);font-size:44px;font-weight:600;color:var(--muted);letter-spacing:-.02em;line-height:1;min-width:64px}
.agz-landing .prin h3{font-size:15.5px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;margin-bottom:12px;color:var(--ink)}
.agz-landing .prin p{color:var(--ink-soft);font-size:16px;line-height:1.65;margin:0}
.agz-landing .closing{margin-top:72px;padding-top:56px;border-top:1px solid var(--line-soft)}
.agz-landing .closing h2{font-size:clamp(26px,3vw,38px);letter-spacing:-.03em;margin-bottom:18px;max-width:820px}
.agz-landing .closing p{color:var(--ink-soft);font-size:17px;line-height:1.65;max-width:800px;margin-bottom:32px}
@media(max-width:520px){.agz-landing .public-page{padding:48px 0 32px}.agz-landing .prin{grid-template-columns:1fr;gap:8px;padding:22px}.agz-landing .prin .n{font-size:32px}}
`;

export default function Manifesto() {
  const nav = useNavigate();
  useSeo({
    title: "The Agenzee Manifesto | A Better Marketing System",
    description: "The principles behind Agenzee: connected strategy, brand consistency, responsible AI and practical marketing execution.",
    path: "/manifesto",
  });
  return (
    <PublicShell>
      <style>{CSS}</style>
      <div className="public-page" data-testid="manifesto-page">
        <div className="wrap">
          <span className="eyebrow">The Agenzee Manifesto</span>
          <h1>Technology should bring clarity to marketing, not more complexity.</h1>
          <p className="intro">
            We believe every business should be able to access structured strategy, consistent communication and high-quality execution without relying on a fragmented collection of tools.
          </p>

          <div className="principles">
            <article className="prin">
              <span className="n">01</span>
              <div>
                <h3>Strategy must come before output</h3>
                <p>AI can generate content quickly, but speed without direction creates noise. Every meaningful output should begin with an understanding of the business, its audience, its position and its objectives.</p>
              </div>
            </article>
            <article className="prin">
              <span className="n">02</span>
              <div>
                <h3>A brand should not have to explain itself twice</h3>
                <p>Brand knowledge should remain available across the entire marketing workflow. Strategy, scripts, creatives and landing pages should all work from the same foundation.</p>
              </div>
            </article>
            <article className="prin">
              <span className="n">03</span>
              <div>
                <h3>Marketing should operate as one system</h3>
                <p>Planning and production cannot remain disconnected. The strategy should inform the action plan. The action plan should inform the content. Every campaign should remain connected to the original business objective.</p>
              </div>
            </article>
            <article className="prin">
              <span className="n">04</span>
              <div>
                <h3>Artificial intelligence should support human judgement</h3>
                <p>AI should accelerate research, planning and production while leaving important decisions visible and controllable. Businesses remain responsible for reviewing claims, information and final materials before publication.</p>
              </div>
            </article>
            <article className="prin">
              <span className="n">05</span>
              <div>
                <h3>Consistency should not limit creativity</h3>
                <p>A strong brand can explore different concepts, formats and campaigns without losing its identity. Brand rules create a foundation from which better creative decisions can be made.</p>
              </div>
            </article>
            <article className="prin">
              <span className="n">06</span>
              <div>
                <h3>Better systems create better work</h3>
                <p>The purpose of automation is not simply to produce more. It is to reduce repetition, preserve knowledge and give people more time to focus on decisions that matter.</p>
              </div>
            </article>
          </div>

          <section className="closing">
            <h2>This is the system we are building.</h2>
            <p>Agenzee connects brand knowledge, strategy, planning and production inside one intelligent workspace, helping businesses move faster while remaining clear, recognizable and in control.</p>
            <button
              className="btn btn--primary btn--lg"
              onClick={() => nav("/auth")}
              data-testid="manifesto-cta-btn">
              Build your marketing system
            </button>
          </section>
        </div>
      </div>
    </PublicShell>
  );
}
